import { ClientOnly } from "@tanstack/react-router";
import { useUnsafeLayoutEffect } from "@wags/tanstack-start/react";
import { edgeScrimRef } from "@wags/ui/browser-edges.tsx";
import { edgeScrim } from "@wags/ui/edge-color.ts";
import { VercelImage } from "@wags/ui/vercel-image.tsx";
import { isVideoMedia } from "@wags/utils/media.ts";
import {
    type ReactNode,
    type RefObject,
    useState,
    useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { PhotoViewerChrome } from "./photo-viewer-chrome.tsx";
import {
    createPhotoViewer,
    type PhotoViewer,
    type ViewerMode,
} from "./photo-viewer-controller.ts";

type Media = { url: string; mimeType?: string | null };

const backdropEdge = edgeScrimRef(edgeScrim("bg-true-black").tint);

export function useDogPhotoViewer() {
    const [viewer] = useState(createPhotoViewer);
    return viewer;
}

function decodeEarly(img: HTMLImageElement | null) {
    img?.decode().catch(() => {});
}

function photoMediaIndexes(media: readonly Media[]) {
    return media.flatMap((item, i) => (isVideoMedia(item) ? [] : [i]));
}

function framesToRender(
    mode: ViewerMode,
    index: number,
    heroPhoto: number,
    total: number,
) {
    if (mode === "inline") return heroPhoto < 0 ? [] : [heroPhoto];
    return [index - 1, index, index + 1].filter((p) => p >= 0 && p < total);
}

function ViewerLayer({
    viewer,
    media,
    mediaIndexes,
    heroPhoto,
    title,
}: {
    viewer: PhotoViewer;
    media: readonly Media[];
    mediaIndexes: readonly number[];
    heroPhoto: number;
    title: string;
}) {
    const {
        subscribe,
        getSnapshot,
        mountRoot,
        mountFrame,
        mountChrome,
        refit,
        close,
    } = viewer;
    const { mode, index } = useSyncExternalStore(
        subscribe,
        getSnapshot,
        getSnapshot,
    );
    const open = mode === "open";
    const frames = framesToRender(mode, index, heroPhoto, mediaIndexes.length);

    return createPortal(
        <div
            ref={mountRoot}
            role={open ? "dialog" : undefined}
            aria-modal={open ? true : undefined}
            aria-label={open ? `${title} photos` : undefined}
            aria-hidden={open ? undefined : true}
        >
            <div
                data-viewer-layer
                className="pointer-events-none fixed inset-0 z-2147483000 pt-[max(12px,env(safe-area-inset-top))] pb-[env(safe-area-inset-bottom)] md:pt-[env(safe-area-inset-top)]"
            >
                <div
                    ref={backdropEdge}
                    data-viewer-backdrop
                    className="absolute inset-0 bg-true-black opacity-0"
                />
                {frames.map((p) => {
                    const mediaIdx = mediaIndexes[p];
                    const item =
                        mediaIdx === undefined ? undefined : media[mediaIdx];
                    if (mediaIdx === undefined || !item) return null;
                    return (
                        <div
                            key={item.url}
                            ref={mountFrame(p)}
                            data-viewer-photo={p}
                            className="invisible absolute top-0 left-0 origin-top-left"
                        >
                            <VercelImage
                                ref={decodeEarly}
                                src={item.url}
                                mimeType={item.mimeType}
                                alt={`${title} photo ${mediaIdx + 1}`}
                                width={1200}
                                height={1500}
                                loading="eager"
                                listingMedia
                                draggable={false}
                                onLoad={() => refit(p)}
                                className="h-full w-full object-contain select-none"
                            />
                        </div>
                    );
                })}
            </div>
            {open ? (
                <PhotoViewerChrome
                    title={title}
                    index={index}
                    total={mediaIndexes.length}
                    contentRef={mountChrome}
                    onClose={close}
                />
            ) : null}
        </div>,
        document.body,
    );
}

export function DogPhotoViewer({
    viewer,
    coveredBy,
    media,
    mediaIndex,
    onMediaIndexChange,
    title,
    children,
}: {
    viewer: PhotoViewer;
    coveredBy: readonly RefObject<Element | null>[];
    media: readonly Media[];
    mediaIndex: number;
    onMediaIndexChange: (mediaIndex: number) => void;
    title: string;
    children: ReactNode;
}) {
    const { attachHero, setConfig } = viewer;
    const mediaIndexes = photoMediaIndexes(media);
    useUnsafeLayoutEffect(() => {
        setConfig({
            coveredBy,
            mediaIndexes: photoMediaIndexes(media),
            mediaIndex,
            onMediaIndexChange,
        });
    }, [setConfig, coveredBy, media, mediaIndex, onMediaIndexChange]);
    return (
        <div
            ref={attachHero}
            className="group h-[calc(100%-var(--listing-media-bottom-inset,0px))] w-full"
        >
            {children}
            {mediaIndexes.length > 0 ? (
                <ClientOnly fallback={null}>
                    <ViewerLayer
                        viewer={viewer}
                        media={media}
                        mediaIndexes={mediaIndexes}
                        heroPhoto={mediaIndexes.indexOf(mediaIndex)}
                        title={title}
                    />
                </ClientOnly>
            ) : null}
        </div>
    );
}
